import React, { useEffect, useState } from "react";
import CommonButton from "../../../components/CommonButton";
import { useLocation, useNavigate } from "react-router-dom";
import BookingCartSummary from "../../../components/customer/BookingCartSummary";
import { ArrowLeft } from "lucide-react";
import { useLazyGetAddOnsQuery } from "../../../redux/actions/Customer/serviceCustomer";
import { AddOn } from "../../../types/Response/CustomerResponse";

const AddGroomingScreen: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const selectedPackage = location.state?.selectedPackage;
    const serviceType = location.state?.serviceType || "grooming";
    const serviceId = location.state?.serviceId || 24;
    const packageId = selectedPackage?.packageId || location.state?.packageId;

    const [triggerGetAddOns, { data: addOnsData, isLoading: isAddOnsLoading }] = useLazyGetAddOnsQuery();
    const [selectedAddOns, setSelectedAddOns] = useState<AddOn[]>([]);

    useEffect(() => {
        if (packageId) {
            triggerGetAddOns(packageId);
        }
    }, [packageId, triggerGetAddOns]);

    const packagePrice =
        selectedPackage?.pricing?.salePrice ||
        selectedPackage?.pricing?.price ||
        0;

    const addOnsPrice = selectedAddOns.reduce((sum, item) => sum + item.price, 0);
    const totalPrice = packagePrice + addOnsPrice;

    const handleToggleAddOn = (item: AddOn) => {
        setSelectedAddOns((prev) => {
            const exists = prev.some((a) => a.addOnId === item.addOnId);
            if (exists) {
                return prev.filter((a) => a.addOnId !== item.addOnId);
            }
            return [...prev, item];
        });
    };

    const handleContinueToDate = () => {
        navigate("/customergroomingbookingdatescreen", {
            state: {
                serviceId,
                packageId: selectedPackage?.packageId,
                selectedPackage,
                selectedAddOns,
                totalPrice,
                serviceType,
            },
        });
    };

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-10">
            <div className="mb-4 sm:mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-logoHeading hover:text-mainBrandColor transition-colors py-1"
                >
                    <ArrowLeft size={20} />
                    <span className="font-medium text-sm sm:text-base">Back</span>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                {/* ADD-ONS LIST PANEL */}
                <div className="lg:col-span-7 xl:col-span-8 bg-white rounded-2xl sm:rounded-[2rem] border border-[#ECE7DF] shadow-sm overflow-hidden">
                    <div className="p-4 sm:p-6 h-full flex flex-col">
                        <h2 className="text-xl sm:text-3xl font-bold text-logoHeading">
                            Add-on Services
                        </h2>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            Make your grooming session even better with these optional services.
                        </p>

                        <div className="mt-4 sm:mt-6 max-h-[calc(100vh-220px)] overflow-y-auto pr-1 sm:pr-2 scrollbar-thin">
                            {isAddOnsLoading ? (
                                <div className="text-center py-8 sm:py-10 text-gray-400 text-sm">Loading add-ons...</div>
                            ) : (addOnsData || []).length === 0 ? (
                                <div className="text-center py-8 sm:py-10 text-gray-400 text-sm">No add-ons available for this service.</div>
                            ) : (
                                <div className="space-y-3 sm:space-y-4">
                                    {(addOnsData || []).map((item: AddOn) => {
                                        const isSelected = selectedAddOns.some((a) => a.addOnId === item.addOnId);

                                        return (
                                            <div
                                                key={item.addOnId}
                                                onClick={() => handleToggleAddOn(item)}
                                                className={`
                                                    flex flex-col sm:flex-row justify-between rounded-xl sm:rounded-2xl border-2 cursor-pointer
                                                    transition-all duration-300 items-stretch sm:items-center overflow-hidden
                                                    ${isSelected
                                                        ? "border-mainBrandColor bg-mainBrandColor/5"
                                                        : "border-[#ECE7DF] hover:border-mainBrandColor"
                                                    }
                                                `}
                                            >
                                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 flex-1">
                                                    <div className="w-full sm:w-28 h-36 sm:h-24 shrink-0">
                                                        <img
                                                            src={item.imageUrl || "https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?w=300"}
                                                            alt={item.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>

                                                    <div className="flex-1 px-3 py-1 sm:p-3">
                                                        <h3 className="font-bold text-logoHeading text-sm sm:text-base">
                                                            {item.name}
                                                        </h3>
                                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                                            {item.description}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between sm:justify-end gap-3 p-3 sm:p-4 border-t sm:border-t-0 border-gray-100 sm:border-none">
                                                    <span className="font-bold text-mainBrandColor text-base sm:text-lg whitespace-nowrap">
                                                        AED {item.price}
                                                    </span>

                                                    <CommonButton
                                                        text={isSelected ? "Added" : "Add"}
                                                        rounded="md"
                                                        variant={isSelected ? "filled" : "outline"}
                                                        textContainerClassName="py-1.5 sm:py-2 px-3 sm:px-4 text-xs sm:text-sm"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* SUMMARY CART PANEL */}
                <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-6 space-y-4">
                    {selectedPackage && (
                        <BookingCartSummary
                            packageItem={selectedPackage}
                            addOns={selectedAddOns}
                            totalPrice={totalPrice}
                        />
                    )}

                    <div className="flex justify-center pt-1">
                        <CommonButton
                            text="Continue to Date & Time"
                            className="w-full"
                            variant="filled"
                            rounded="md"
                            onClick={handleContinueToDate}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddGroomingScreen;
